import type { AppRouter } from '@sitzmark/api';
import type { inferRouterOutputs } from '@trpc/server';

/**
 * The import wizard, as a pure state machine (SCOPE §5.1).
 *
 * Kept out of the components on purpose. The step budget in §2.2 — "import a
 * vendor file (saved template): ≤5 interactions" — is a property of this
 * reducer, so it can be *tested* rather than estimated in a design review.
 *
 * `blockedReason` is the other half: §2.2-7 says illegal actions are disabled
 * with a visible reason rather than error-dialogued after the fact, so every
 * gate here returns the sentence the button will show.
 */

type Outputs = inferRouterOutputs<AppRouter>;

export type AnalyseResult = Outputs['import']['analyse'];
export type PreviewResult = Outputs['import']['preview'];
export type CommitResult = Outputs['import']['commit'];
export type SetupData = Outputs['import']['setup'];

export type ColumnRef = { readonly index: number; readonly label: string };
export type ImportMapping = Record<string, ColumnRef | undefined>;

export type WizardStep = 'file' | 'confirm' | 'preview' | 'done';

export interface ChosenFile {
  readonly name: string;
  /** Base64. Held in memory so preview and commit are the same bytes. */
  readonly content: string;
  readonly sizeBytes: number;
}

export interface WizardState {
  readonly step: WizardStep;
  readonly file: ChosenFile | null;
  readonly analysis: AnalyseResult | null;
  readonly vendorId: string | null;
  readonly sheetName: string | null;
  readonly mapping: ImportMapping;
  readonly skipZeroQuantity: boolean;
  /** The category every new style is filed in, at any level (decision 214). */
  readonly departmentId: string | null;
  /** The gender every new style takes — none unless chosen. */
  readonly categoryId: string | null;
  /**
   * Per-style genders and per-style categories, for the styles the operator
   * filed by hand. (`categories` is the gender table's old name.)
   *
   * Anything absent takes the defaults above. That fallback is what keeps the
   * ≤5-interaction budget intact: this is a *refinement* on the preview
   * screen, never a step, so an import that does not care still costs
   * file → preview → create.
   *
   * Keyed by `styleKey`, which is derived from the file's own contents — so
   * they are invalidated by anything that changes how the file is read, and
   * cleared below wherever the preview is.
   */
  readonly categoryByStyleKey: Readonly<Record<string, string>>;
  readonly departmentByStyleKey: Readonly<Record<string, string>>;
  readonly season: string | null;
  readonly saveTemplateAs: string | null;
  readonly preview: PreviewResult | null;
  readonly result: CommitResult | null;
  /** Counts real user actions, so the step budget can be asserted. */
  readonly interactions: number;
}

export const initialWizardState: WizardState = {
  step: 'file',
  file: null,
  analysis: null,
  vendorId: null,
  sheetName: null,
  mapping: {},
  skipZeroQuantity: true,
  departmentId: null,
  categoryId: null,
  categoryByStyleKey: {},
  departmentByStyleKey: {},
  season: null,
  saveTemplateAs: null,
  preview: null,
  result: null,
  interactions: 0,
};

export type WizardEvent =
  | { readonly type: 'file-chosen'; readonly file: ChosenFile }
  | { readonly type: 'analysed'; readonly analysis: AnalyseResult }
  | { readonly type: 'vendor-chosen'; readonly vendorId: string }
  | { readonly type: 'sheet-chosen'; readonly sheetName: string }
  | { readonly type: 'mapping-changed'; readonly field: string; readonly column: ColumnRef | null }
  | { readonly type: 'skip-zero-quantity-changed'; readonly value: boolean }
  | { readonly type: 'department-chosen'; readonly departmentId: string }
  | {
      /** Give these styles this gender (null: none). The bulk move, and the single one. */
      readonly type: 'styles-assigned';
      readonly styleKeys: readonly string[];
      readonly categoryId: string | null;
    }
  | {
      /** File these styles in this category (null: the import's). */
      readonly type: 'styles-filed';
      readonly styleKeys: readonly string[];
      readonly departmentId: string | null;
    }
  | { readonly type: 'season-changed'; readonly season: string | null }
  | { readonly type: 'save-template-changed'; readonly name: string | null }
  | { readonly type: 'previewed'; readonly preview: PreviewResult }
  | { readonly type: 'committed'; readonly result: CommitResult }
  | { readonly type: 'back' }
  | { readonly type: 'restart' };

/**
 * Events the *system* raises after work completes, as opposed to something the
 * operator did. Only operator actions count against the step budget.
 */
const SYSTEM_EVENTS = new Set<WizardEvent['type']>(['analysed', 'previewed', 'committed']);

export function wizardReducer(state: WizardState, event: WizardEvent): WizardState {
  const next = applyEvent(state, event);
  if (next === state) return state;

  return SYSTEM_EVENTS.has(event.type) ? next : { ...next, interactions: state.interactions + 1 };
}

function applyEvent(state: WizardState, event: WizardEvent): WizardState {
  switch (event.type) {
    case 'file-chosen':
      // A new file invalidates everything downstream; nothing should survive
      // from the previous one and quietly get committed.
      return { ...initialWizardState, file: event.file, interactions: state.interactions };

    case 'analysed': {
      const primary =
        event.analysis.sheets.find((sheet) => sheet.name === event.analysis.primarySheet) ??
        event.analysis.sheets.find((sheet) => sheet.recommended) ??
        null;

      // Smart defaults (§2.2-5): a confidently matched template pre-selects its
      // vendor, so the common path is a confirmation rather than data entry.
      const vendorId =
        event.analysis.template.status === 'matched' ? event.analysis.template.vendorId : null;

      return {
        ...state,
        step: 'confirm',
        analysis: event.analysis,
        vendorId,
        sheetName: primary?.name ?? null,
        mapping: (primary?.mapping ?? {}) as ImportMapping,
      };
    }

    case 'vendor-chosen':
      return {
        ...state,
        vendorId: event.vendorId,
        preview: null,
        categoryByStyleKey: {},
        departmentByStyleKey: {},
      };

    case 'sheet-chosen': {
      const sheet = state.analysis?.sheets.find((entry) => entry.name === event.sheetName);
      return {
        ...state,
        sheetName: event.sheetName,
        mapping: (sheet?.mapping ?? {}) as ImportMapping,
        preview: null,
        categoryByStyleKey: {},
        departmentByStyleKey: {},
      };
    }

    case 'mapping-changed': {
      const mapping = { ...state.mapping };
      if (event.column === null) delete mapping[event.field];
      else mapping[event.field] = event.column;
      // Any mapping change makes the existing preview a lie — and the style
      // keys are derived from the file as it is being read, so assignments made
      // against the old reading would silently attach to the wrong styles.
      return { ...state, mapping, preview: null, categoryByStyleKey: {}, departmentByStyleKey: {} };
    }

    case 'skip-zero-quantity-changed':
      return {
        ...state,
        skipZeroQuantity: event.value,
        preview: null,
        categoryByStyleKey: {},
        departmentByStyleKey: {},
      };

    case 'department-chosen':
      /*
       * Styles filed by hand survive this, deliberately: one vendor order is
       * routinely jackets and helmets together, so the picker chooses the
       * category for everything left *unfiled*, and nothing more.
       */
      return { ...state, departmentId: event.departmentId };

    case 'styles-assigned': {
      if (event.styleKeys.length === 0) return state;
      const categoryByStyleKey = { ...state.categoryByStyleKey };
      for (const styleKey of event.styleKeys) {
        if (event.categoryId === null) delete categoryByStyleKey[styleKey];
        else categoryByStyleKey[styleKey] = event.categoryId;
      }
      return { ...state, categoryByStyleKey };
    }

    case 'styles-filed': {
      if (event.styleKeys.length === 0) return state;
      const departmentByStyleKey = { ...state.departmentByStyleKey };
      for (const styleKey of event.styleKeys) {
        if (event.departmentId === null) delete departmentByStyleKey[styleKey];
        else departmentByStyleKey[styleKey] = event.departmentId;
      }
      return { ...state, departmentByStyleKey };
    }

    case 'season-changed':
      return { ...state, season: event.season };

    case 'save-template-changed':
      return { ...state, saveTemplateAs: event.name };

    case 'previewed':
      return { ...state, step: 'preview', preview: event.preview };

    case 'committed':
      return { ...state, step: 'done', result: event.result };

    case 'back':
      if (state.step === 'preview') return { ...state, step: 'confirm' };
      if (state.step === 'confirm')
        return { ...initialWizardState, interactions: state.interactions };
      return state;

    case 'restart':
      return { ...initialWizardState, interactions: state.interactions };

    default:
      return state;
  }
}

/**
 * Why the operator cannot move on yet, or null when they can.
 *
 * Matching needs *some* identity column — a UPC or a vendor part number.
 * Without one, every row would import as a brand-new product and the catalog
 * would double in size (SCOPE §5.1-4).
 */
export function previewBlockedReason(state: WizardState): string | null {
  if (state.file === null) return 'Choose a vendor file first';
  if (state.analysis === null) return 'Reading the file…';
  if (state.vendorId === null) return 'Choose which vendor this order is from';
  if (state.sheetName === null) return 'Choose which sheet holds the order';

  const hasIdentity =
    state.mapping.upc !== undefined || state.mapping.vendorPartNumber !== undefined;
  if (!hasIdentity) {
    return 'Map a UPC or a vendor part number so items can be matched to the catalog';
  }
  if (state.mapping.quantity === undefined) return 'Map the column holding order quantities';
  if (state.mapping.cost === undefined) return 'Map the cost column';

  return null;
}

export function commitBlockedReason(state: WizardState): string | null {
  if (state.preview === null) return 'Preview the import first';
  if (state.preview.totals.rows === 0) {
    return 'Nothing to import — every row was filtered out';
  }
  // New styles need somewhere to live; existing ones already have a home.
  if (state.preview.totals.newStyles > 0 && state.departmentId === null) {
    return 'Choose a category for the new models';
  }
  return null;
}

/** Fields the confirm screen shows up front; the rest sit behind "More" (§2.2-2). */
export const PRIMARY_FIELDS = [
  'upc',
  'styleNumber',
  'styleName',
  'colorName',
  'size',
  'quantity',
  'cost',
  'msrp',
] as const;

export const SECONDARY_FIELDS = ['colorCode', 'altSize', 'vendorPartNumber', 'shipDate'] as const;

/** Every field the confirm screen can show, primary or behind "More". */
export type ImportFieldName = (typeof PRIMARY_FIELDS)[number] | (typeof SECONDARY_FIELDS)[number];

export const FIELD_LABELS: Readonly<Record<string, string>> = {
  upc: 'UPC / barcode',
  styleNumber: 'Model number',
  styleName: 'Model name',
  colorName: 'Color',
  colorCode: 'Color code',
  size: 'Size',
  altSize: 'Alt size / size range',
  vendorPartNumber: 'Vendor part #',
  quantity: 'Quantity ordered',
  cost: 'Our cost',
  msrp: 'MSRP',
  shipDate: 'Ship date',
};
